import { createSlice } from "@reduxjs/toolkit";
import { genericStringSplitter } from "../../util/stringUtils";
import {
  QueryBuilders,
  ActionableQuery,
  QueryableObject,
  parseQuery,
  ValueParentQuery,
} from "../../from-server/asin-central/search";
import { getDomainFromUrl } from "../../util/urlUtils";
import { MULTISELECT_PROPERTY_KEYS } from "./searchPropertyFilter/SearchPropertyFilter-config";
const { or, and, range, filter, fuzzy, val, all } = QueryBuilders;

// Lots of queries we can legally parse out of the url are not representable with
// the search side bar UI.  Instead of throwing, we're noting that these queries
// can't be represented in the UI and executing them anyway.  This allows sophisticated
// users to make complex queries and send them to users as links.
type Unrepresentable = "unrepresentable";

const UNREPRESENTABLE_AND_BYPASS = ["asinType"];

const separator = ",";

export type Range = {
  lower: Date;
  upper: Date;
};

export type ClientQuery = ActionableQuery<QueryableObject>;

// the keys are the attribute name, the values are queries for that attribute along with the UI state
export type UIFilters = {
  [key: string]: { value: string | Range }; //query: ClientQuery;
};

// PropertyFilter and PropertyFilterGroup types allow to build nested queries using the UI
export type PropertyFilter = {
  type: "PropertyFilter";
  operator: "filter" | "fuzzy";
  value: any;
  propertyKey: string;
  /**
   * Session-scoped provenance marker. When set, identifies the token
   * as authored by a UI feature that owns the (propertyKey, value)
   * pair and needs to remove it later without touching user-authored
   * tokens that happen to share the same (propertyKey, value).
   *
   * Consumed only by the authoring feature (currently the Step
   * Breakdown pivot); read as `undefined` everywhere else.
   * `sumPropertyFilterQueries` ignores extra fields, so the marker
   * never reaches DACS or the URL query string.
   */
  internalSource?: "step-drilldown";
};
export type PropertyFilterGroup = {
  type: "PropertyFilterGroup";
  filters: Array<PropertyFilterGroup | PropertyFilter>;
  operation: "and" | "or";
};

const DEFAULT_PROPERTY_FILTER_GROUP_VALUE: PropertyFilterGroup = {
  type: "PropertyFilterGroup",
  filters: [],
  operation: "and",
};

const LOW_DATE = new Date("1000-01-01");
const HIGH_DATE = new Date("9999-01-01");

function stringArrayToQuery(
  attribute: string,
  strs: string[]
): ClientQuery | undefined {
  if (strs.length === 0) {
    return undefined;
  }
  const eachValue = strs.map((s) => filter(val(attribute, s.trim())));
  return or(eachValue[0], ...eachValue.slice(1));
}

function stringArrayToQueryWithOp(
  attribute: string,
  strs: string[],
  operator: "filter" | "fuzzy"
): ClientQuery | undefined {
  if (strs.length === 0) {
    return undefined;
  }
  const eachValue = strs.map((s) => {
    switch (operator) {
      case "filter":
        return filter(val(attribute, sanitizeInputFilters(s.trim())));
      case "fuzzy":
        return fuzzy(val(attribute, sanitizeInputFilters(s.trim())));
      default:
        return filter(val(attribute, sanitizeInputFilters(s.trim())));
    }
  });
  return or(eachValue[0], ...eachValue.slice(1));
}

export function stringToQuery(
  attribute: string,
  str?: string | string[]
): ClientQuery | undefined {
  if (typeof str !== "string" || str === "") {
    return undefined;
  }
  if (Array.isArray(str)) {
    return stringArrayToQuery(attribute, str);
  }
  if (str.indexOf(separator)) {
    return stringArrayToQuery(attribute, str.split(separator));
  }
  return filter(val(attribute, str.trim()));
}

export function stringToQueryWithOp(
  attribute: string,
  str?: string | string[],
  operator: "filter" | "fuzzy" = "filter"
): ClientQuery | undefined {
  if (typeof str !== "string" || str === "") {
    return undefined;
  }
  if (Array.isArray(str)) {
    return stringArrayToQueryWithOp(attribute, str, operator);
  }
  if (str.indexOf(separator) > -1) {
    return stringArrayToQueryWithOp(attribute, str.split(separator), operator);
  }
  switch (operator) {
    case "filter":
      return filter(val(attribute, sanitizeInputFilters(str.trim())));
    case "fuzzy":
      return fuzzy(val(attribute, sanitizeInputFilters(str.trim())));
    default:
      return filter(val(attribute, sanitizeInputFilters(str.trim())));
  }
}

export function datesToQuery(
  attribute: string,
  rangeState: Range
): ClientQuery | undefined {
  if (!rangeState.lower && !rangeState.upper) {
    return undefined;
  }
  const low = rangeState.lower || LOW_DATE;
  const high = rangeState.upper || HIGH_DATE;
  return range(val(attribute, low), val(attribute, high));
}

export const defaultQueryState: QueryState = {
  unrepresentable: false,
  queryMode: "UIFilter",
  queryPropertyFilters: DEFAULT_PROPERTY_FILTER_GROUP_VALUE,
  queryPropertyFiltersForceRehydrate: false,
  queryPropertyFiltersRehydrateSignal: 0,
  activePropertyFilters: {},
  activePropertyFiltersVisibilityEnabled: false,
  queryParameters: {},
  queryString: all().toString(),
};

/**
 * True when `group` contains at least one populated leaf filter.
 * Empty strings, `undefined`, `null`, and empty nested groups all
 * count as "no filter". Used to gate Submit and to drop the active
 * saved/predefined selection once the last chip is removed.
 */
export function hasAnyFilter(group: PropertyFilterGroup | undefined): boolean {
  if (!group || !group.filters || group.filters.length === 0) return false;
  return group.filters.some((f: PropertyFilter | PropertyFilterGroup) => {
    if (f.type === "PropertyFilter") {
      if (typeof f.value === "string") return f.value.trim().length > 0;
      return f.value !== undefined && f.value !== null;
    }
    return hasAnyFilter(f);
  });
}

export function sumPropertyFilterQueries(
  query: PropertyFilterGroup
): ClientQuery {
  if (!query || Object.keys(query).length === 0) return all();
  const subQueries: ClientQuery[] = [];
  query?.filters.forEach(
    (customFilter: PropertyFilterGroup | PropertyFilter) => {
      if (customFilter.type === "PropertyFilterGroup") {
        subQueries.push(sumPropertyFilterQueries(customFilter));
      }
      if (customFilter.type === "PropertyFilter") {
        if (customFilter.propertyKey === "asin") {
          const value = genericStringSplitter(
            customFilter.value as string
          ).join(separator);
          const clientQuery = stringToQueryWithOp(
            customFilter.propertyKey,
            value,
            customFilter.operator
          );
          if (clientQuery) {
            subQueries.push(clientQuery);
          }
        } else {
          const clientQuery =
            typeof customFilter.value === "string"
              ? stringToQueryWithOp(
                  customFilter.propertyKey,
                  customFilter.value,
                  customFilter.operator
                )
              : datesToQuery(customFilter.propertyKey, customFilter.value);
          if (clientQuery) {
            subQueries.push(clientQuery);
          }
        }
      }
    }
  );
  if (subQueries.length === 0) return all();
  return query.operation === "or"
    ? or(subQueries[0], ...subQueries.slice(1))
    : and(subQueries[0], ...subQueries.slice(1));
}

function sumQueries(filters: UIFilters): ClientQuery {
  const subQueries = Object.entries(filters)
    .map(([attributeName, value]) => {
      if (attributeName === "asin") {
        // Allow a poorly formatted list of ASIN's similar to SelectionCentral (see LEAP-7186).
        value.value = genericStringSplitter(value.value as string).join(
          separator
        );
      }
      if (attributeName === "asinType" && typeof value.value === "string") {
        // The UI ASIN's type filter groups types by party affiliation.
        // Most ASIN types have a one-to-one relationship with their party attribute or are party-agnostic.
        // However, the "Non MFA (1p)" and "Non MFA (2p)" ASIN types are special cases, as both of them use the same ASIN type key "non_mfa" and they are not party agnostic
        // To allow consistent filtering, we need to introduce two virtual keys in the UI ASIN's type filter: "1p_non_mfa" and "2p_non_mfa".
        // These virtual keys will be processed internally by joining the asinType and asinParty, enabling us to filter both "Non MFA (1p)" and "Non MFA (2p)" ASIN types simultaneously.
        const defaultSubQueries =
          stringToQuery(attributeName, value.value)?.getSubQueries() ?? [];
        const nonMFASubQueries = [];
        if (value.value.indexOf("1p_non_mfa") !== -1) {
          nonMFASubQueries.push(
            and(
              filter(val("asinType", "non_mfa")),
              filter(val("asinParty", "1p"))
            )
          );
        }
        if (value.value.indexOf("2p_non_mfa") !== -1) {
          nonMFASubQueries.push(
            and(
              filter(val("asinType", "non_mfa")),
              filter(val("asinParty", "2p"))
            )
          );
        }
        const asinTypeSubQueries = [...defaultSubQueries, ...nonMFASubQueries];
        if (asinTypeSubQueries.length === 0) return undefined;
        return or(asinTypeSubQueries[0], ...asinTypeSubQueries.slice(1));
      }
      if (value !== undefined && value.value !== undefined) {
        return typeof value?.value === "string"
          ? stringToQuery(attributeName, value.value)
          : datesToQuery(attributeName, value.value);
      }
      return undefined;
    })
    .filter((q) => q !== undefined) as ClientQuery[];
  return subQueries.length > 0
    ? and(subQueries[0], ...subQueries.slice(1))
    : all();
}

function getAttributeName(sq: ClientQuery): string {
  if (sq.type === "or" || sq.type === "and") {
    return getAttributeName(sq.getSubQueries()[0]);
  }
  return (sq as ValueParentQuery<any>).getValues()[0].attribute;
}

function getValue(sq: ClientQuery): string | Range {
  if (sq.type === "or") {
    return sq
      .getSubQueries()
      .map((q) => getValue(q))
      .filter((value) => value !== "")
      .join(", ");
  }
  if (
    sq.type === "and" &&
    UNREPRESENTABLE_AND_BYPASS.includes(getAttributeName(sq))
  )
    return "";
  if (sq.type === "range") {
    const values = (sq as ValueParentQuery<any>).getValues();
    return {
      upper: values[1].value as Date,
      lower: values[0].value as Date,
    };
  }
  return (sq as ValueParentQuery<any>).getValues()[0].value as string;
}

function spreadPropertyFilterQueries(
  query: ClientQuery,
  depth = 0
): PropertyFilterGroup | Unrepresentable {
  const subQueries: Array<PropertyFilterGroup | PropertyFilter> = [];
  if (depth === 0) {
    if (query.type === "all") {
      return DEFAULT_PROPERTY_FILTER_GROUP_VALUE;
    }
    if (query.type !== "and") {
      // Property filter component can only represent queries where the root is an AND
      return "unrepresentable";
    }
    if (query.getSubQueries().length > 2) {
      // Root element must have at most two sub queries (ASIN filter and PropertyFilter queries)
      return "unrepresentable";
    }
  }
  if (depth > 2) {
    // Property filter support at most root level + two nested levels
    return "unrepresentable";
  }
  for (let subQuery of query.getSubQueries()) {
    if (subQuery.type === "and" || subQuery.type === "or") {
      if (depth === 0) {
        // Process multiple ASINs filter if apply
        if (subQuery.type === "or" && getAttributeName(subQuery) === "asin") {
          const value = getValue(subQuery);
          subQueries.push({
            propertyKey: "asin",
            value: value ?? "",
            operator: "filter",
            type: "PropertyFilter",
          });
          continue;
        }
      }
      const propertyFilterGroup = spreadPropertyFilterQueries(
        subQuery,
        depth + 1
      );
      if (propertyFilterGroup === "unrepresentable") {
        return "unrepresentable";
      }
      if (propertyFilterGroup?.filters?.length > 0) {
        subQueries.push(propertyFilterGroup);
      }
    } else {
      const attribute = getAttributeName(subQuery);
      const value = getValue(subQuery);
      subQueries.push({
        propertyKey: attribute,
        value: value ?? "",
        operator: subQuery?.type ?? "filter",
        type: "PropertyFilter",
      } as PropertyFilter);
    }
  }
  return {
    type: "PropertyFilterGroup",
    filters: subQueries,
    operation: query?.type === "or" ? "or" : "and",
  };
}

function spreadQueries(query: ClientQuery): UIFilters | Unrepresentable {
  if (query.type === "all") {
    return {};
  }
  // the search side bar component can only represent queries where the root is an AND
  if (query.type !== "and") {
    return "unrepresentable";
  }
  const subQueries = query.getSubQueries();
  const uiLegalSecondGenNodes = new Set(["or", "range", "filter", "fuzzy"]);
  if (
    subQueries.filter((sq) => !uiLegalSecondGenNodes.has(sq.type)).length > 0
  ) {
    return "unrepresentable";
  }
  if (subQueries.length < 1) {
    return "unrepresentable";
  }
  return subQueries.reduce((uiFilters: UIFilters, sq) => {
    uiFilters[getAttributeName(sq)] = {
      value: getValue(sq),
    };
    return uiFilters;
  }, {});
}

function parsePropertyFilterQueryClientSide(
  query: string
): PropertyFilterGroup | Unrepresentable {
  // Flatten `or(or(...), ...)` chains that all target the same
  // multi-select enum property (e.g. `asinType`) into a single flat
  // `or(filter, filter, ...)` before spreading. Nested OR-of-OR
  // arrangements can be produced by Polaris' token-group UI when a
  // user drops two enum tokens for the same property into a nested
  // group, and they otherwise trip the depth-2 limit in
  // `spreadPropertyFilterQueries` and render as an empty (default)
  // filter tree with the query still executing on the backend.
  return spreadPropertyFilterQueries(
    flattenEnumOrsInClientQuery(parseQuery(query))
  );
}

/**
 * Mirror of {@link flattenEnumOrsInClientQuery} for the internal
 * `PropertyFilter`/`PropertyFilterGroup` tree. Recursively flattens
 * `PropertyFilterGroup{or, ...}` chains whose leaves all target a
 * single `MULTISELECT_PROPERTY_KEYS` property into one flat
 * `PropertyFilterGroup{or, filters: [PF, PF, ...]}`. Called on the
 * emit side (`PropertyFilterInput`) before dispatch so we never
 * store — or serialise into the URL — an OR-of-OR arrangement that
 * would otherwise trip the depth-2 limit on the parse side.
 */
export function flattenEnumOrGroupsInternal(
  group: PropertyFilterGroup
): PropertyFilterGroup {
  // Recurse first, and unwrap any single-leaf OR returned by a nested
  // call into a bare `PropertyFilter`. Mirrors the ClientQuery-side
  // flatten, which returns the bare leaf when only one survives dedup
  // — this one can only unwrap during recursion (not at the return),
  // since the function is typed to return a `PropertyFilterGroup`.
  const children: Array<PropertyFilter | PropertyFilterGroup> = [];
  for (const child of group.filters ?? []) {
    if (child.type !== "PropertyFilterGroup") {
      children.push(child);
      continue;
    }
    const flattened = flattenEnumOrGroupsInternal(child);
    if (
      flattened.operation === "or" &&
      flattened.filters.length === 1 &&
      flattened.filters[0].type === "PropertyFilter"
    ) {
      children.push(flattened.filters[0]);
    } else {
      children.push(flattened);
    }
  }
  if (group.operation === "or") {
    const leaves = collectSameEnumInternalLeaves(children);
    if (leaves && leaves.length > 0) {
      return {
        type: "PropertyFilterGroup",
        operation: "or",
        filters: dedupePropertyFiltersByValue(leaves),
      };
    }
  }
  return { ...group, filters: children };
}

/**
 * Dedupe `PropertyFilter`s by value so a repeated `(propertyKey,
 * value)` pair (e.g. from a hand-crafted `or(filter(k,v),
 * filter(k,v))` URL) doesn't survive flattening into the flat OR
 * group — otherwise the emit side's `maybeCollapseToEnumToken`
 * would build an enum token with a duplicated value. Callers
 * guarantee all filters share one `propertyKey`, so keying by
 * value alone is sufficient.
 */
function dedupePropertyFiltersByValue(
  filters: PropertyFilter[]
): PropertyFilter[] {
  const seen = new Set<string>();
  const out: PropertyFilter[] = [];
  for (const f of filters) {
    const vk = typeof f.value === "string" ? f.value : JSON.stringify(f.value);
    if (seen.has(vk)) continue;
    seen.add(vk);
    out.push(f);
  }
  return out;
}

function collectSameEnumInternalLeaves(
  nodes: Array<PropertyFilter | PropertyFilterGroup>
): PropertyFilter[] | null {
  const collected: PropertyFilter[] = [];
  let key: string | null = null;
  for (const node of nodes) {
    if (node.type === "PropertyFilter") {
      if (!MULTISELECT_PROPERTY_KEYS.has(node.propertyKey)) return null;
      if (node.operator !== "filter") return null;
      if (key === null) key = node.propertyKey;
      else if (node.propertyKey !== key) return null;
      collected.push(node);
    } else if (node.type === "PropertyFilterGroup" && node.operation === "or") {
      const sub = collectSameEnumInternalLeaves(node.filters ?? []);
      if (sub === null) return null;
      for (const leaf of sub) {
        if (key === null) key = leaf.propertyKey;
        else if (leaf.propertyKey !== key) return null;
        collected.push(leaf);
      }
    } else {
      return null;
    }
  }
  return collected;
}

/**
 * Recursively rewrite `or(...)` nodes whose transitive leaves are
 * all `filter(attr,v)` clauses on a single `MULTISELECT_PROPERTY_KEYS`
 * attribute into one flat `or(filter, filter, ...)`. Mixed-property
 * ORs, non-enum ORs, and any tree that reaches a non-`or`/non-`filter`
 * node are left as-is; we still recurse into their children so
 * matching chains buried inside AND branches also get flattened.
 */
function flattenEnumOrsInClientQuery(query: ClientQuery): ClientQuery {
  if (query.type === "or") {
    const flat = collectSameEnumFilterLeaves(query);
    if (flat && flat.length > 0) {
      // Dedupe by value so `filter(k,v) OR filter(k,v)` collapses to
      // a single leaf — mirror of the internal-side dedup above.
      const seen = new Set<string>();
      const deduped: ClientQuery[] = [];
      for (const leaf of flat) {
        const v = getValue(leaf);
        const vk = typeof v === "string" ? v : JSON.stringify(v);
        if (seen.has(vk)) continue;
        seen.add(vk);
        deduped.push(leaf);
      }
      return deduped.length === 1
        ? deduped[0]
        : or(deduped[0], ...deduped.slice(1));
    }
  }
  if (query.type === "and" || query.type === "or") {
    const subs = query
      .getSubQueries()
      .map((s: ClientQuery) => flattenEnumOrsInClientQuery(s));
    return query.type === "and"
      ? and(subs[0], ...subs.slice(1))
      : or(subs[0], ...subs.slice(1));
  }
  return query;
}

/**
 * Returns the flat list of leaf `filter` nodes if `query` is either
 * a `filter` on a multi-select enum property, or an `or` whose
 * subtree contains only such leaves (all on the same property).
 * Returns `null` when any branch violates that shape so the caller
 * preserves the original structure.
 */
function collectSameEnumFilterLeaves(query: ClientQuery): ClientQuery[] | null {
  if (query.type === "filter") {
    const attr = getAttributeName(query);
    if (!MULTISELECT_PROPERTY_KEYS.has(attr)) return null;
    return [query];
  }
  if (query.type === "or") {
    const collected: ClientQuery[] = [];
    let attr: string | null = null;
    for (const sub of query.getSubQueries()) {
      const subLeaves = collectSameEnumFilterLeaves(sub);
      if (subLeaves === null) return null;
      for (const leaf of subLeaves) {
        const leafAttr = getAttributeName(leaf);
        if (attr === null) attr = leafAttr;
        else if (leafAttr !== attr) return null;
        collected.push(leaf);
      }
    }
    return collected;
  }
  return null;
}

/**
 * Parse an applied queryString back to its PropertyFilterGroup form.
 * Returns `undefined` for queries that can't be round-tripped
 * through the property-filter tree (legacy UIFilter-only queries or
 * clauses the property-filter parser deliberately drops), and also
 * for queries that throw during parse. Callers are expected to fall
 * back to the raw queryString in that case. Safe to call inside a
 * render / useMemo since it never throws.
 */
export function parsePropertyFilterFromQueryString(
  query: string
): PropertyFilterGroup | undefined {
  try {
    const parsed = parsePropertyFilterQueryClientSide(query);
    return parsed === "unrepresentable" ? undefined : parsed;
  } catch {
    return undefined;
  }
}

function parseQueryClientSide(query: string): UIFilters | Unrepresentable {
  return spreadQueries(parseQuery(query));
}

/**
 * `queryPropertyFiltersRehydrateSignal` is owned by the reducers,
 * not by URL parsing — the URL doesn't carry a rehydrate epoch. Any
 * caller that assigns the parsed state onto the slice is responsible
 * for bumping the signal separately if it wants PropertyFilterInput
 * to rehydrate.
 */
type ParsedQueryState = Omit<QueryState, "queryPropertyFiltersRehydrateSignal">;

function getStateFromString(
  query: string,
  queryMode: "UIFilter" | "PropertyFilter" = "UIFilter"
): ParsedQueryState {
  try {
    // Default UI Filters
    if (queryMode === "UIFilter") {
      const parsedUIFilters = parseQueryClientSide(query);
      if (parsedUIFilters !== "unrepresentable") {
        return {
          unrepresentable: false,
          queryMode: queryMode,
          queryPropertyFilters: DEFAULT_PROPERTY_FILTER_GROUP_VALUE,
          queryPropertyFiltersForceRehydrate: false,
          activePropertyFilters: {},
          activePropertyFiltersVisibilityEnabled: false,
          queryParameters: parsedUIFilters,
          queryString: sumQueries(parsedUIFilters).toString(),
          queryParseError: undefined,
        };
      }
    }
    // Property Filters
    if (queryMode === "PropertyFilter") {
      const parsedPropertyFilters = parsePropertyFilterQueryClientSide(query);
      if (parsedPropertyFilters !== "unrepresentable") {
        return {
          unrepresentable: false,
          queryMode: queryMode,
          queryParameters: {},
          queryPropertyFilters: parsedPropertyFilters,
          queryPropertyFiltersForceRehydrate: true,
          activePropertyFilters: {},
          activePropertyFiltersVisibilityEnabled: false,
          queryString: sumPropertyFilterQueries(
            parsedPropertyFilters
          ).toString(),
          queryParseError: undefined,
        };
      }
    }
    // Unrepresentable Filters
    return {
      unrepresentable: true,
      queryMode: queryMode,
      queryPropertyFilters: DEFAULT_PROPERTY_FILTER_GROUP_VALUE,
      queryPropertyFiltersForceRehydrate: false,
      activePropertyFilters: {},
      activePropertyFiltersVisibilityEnabled: false,
      queryParameters: {},
      queryString: query,
      queryParseError: undefined,
    };
  } catch (e: any) {
    return {
      unrepresentable: true,
      queryMode: queryMode,
      queryPropertyFilters: DEFAULT_PROPERTY_FILTER_GROUP_VALUE,
      queryPropertyFiltersForceRehydrate: false,
      activePropertyFilters: {},
      activePropertyFiltersVisibilityEnabled: false,
      queryParameters: {},
      queryString: query,
      queryParseError: (e as Error).toString(),
    };
  }
}

function getStartingQuery(): QueryState {
  if (typeof window === "object") {
    const queryFromUrl = window.location.href.split("#")[1];
    if (queryFromUrl !== undefined && queryFromUrl.trim() !== "") {
      const queryUri = decodeURI(queryFromUrl.split("(queryMode)")[0]);
      const queryMode = queryFromUrl.includes("(queryMode)=PropertyFilter")
        ? "PropertyFilter"
        : "UIFilter";
      const parsed = getStateFromString(queryUri, queryMode);
      // App-mount PropertyFilterInput will read the initial state and
      // rehydrate on its own [] effect; seed the signal to 1 so any
      // subsequent same-value bump is still a distinct edge.
      return {
        ...parsed,
        queryPropertyFiltersRehydrateSignal:
          parsed.queryPropertyFiltersForceRehydrate ? 1 : 0,
      };
    }
  }
  return defaultQueryState;
}

function writeQueryToUrl(
  query: string,
  queryMode: "UIFilter" | "PropertyFilter" = "UIFilter"
) {
  //budget routing
  if (typeof window === "object") {
    const domain = getDomainFromUrl();
    window.location.replace(`${domain}#${query}(queryMode)=${queryMode}`);
  }
}

/** Strip the URL hash silently (no popstate, no page reload). */
function clearQueryFromUrl() {
  if (typeof window === "object" && window.location.hash) {
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search
    );
  }
}

// Sanitize input text to match with expected search.ts valueTupleMatcher
// This helps to reduce 500 errors when characters are not allowed by valueTupleMatcher
const sanitizeInputFilters = (value: string): string => {
  if (!value || value === "") return "";
  // Replace all characters that don't match the allowed pattern
  return value.replace(/[^\w\s\-.,:+\/|]/g, "-");
};

type QueryState = {
  unrepresentable: boolean;
  queryMode: "PropertyFilter" | "UIFilter";
  queryPropertyFilters: PropertyFilterGroup;
  queryPropertyFiltersForceRehydrate: boolean;
  /**
   * Monotonic counter that increments every time an external writer
   * changes queryPropertyFilters and wants PropertyFilterInput to
   * push the new tree into its Polaris local state. Unlike the
   * boolean forceRehydrate flag (which the consumer resets and
   * therefore races with concurrent producers under React 17's
   * unbatched-effect semantics), the counter only ever increases —
   * so any producer, at any time, can trigger a rehydrate without
   * coordinating with the consumer.
   */
  queryPropertyFiltersRehydrateSignal: number;
  activePropertyFilters: { [key: string]: boolean };
  activePropertyFiltersVisibilityEnabled: boolean;
  queryParameters: UIFilters;
  queryString: string;
  pendingQueryString?: string;
  queryParseError?: string;
};

const generateQuery = createSlice({
  name: "queryParameters",
  initialState: getStartingQuery(),
  reducers: {
    resetFilter(state) {
      state.unrepresentable = false;
      state.queryParameters = {};
      state.queryPropertyFilters = DEFAULT_PROPERTY_FILTER_GROUP_VALUE;
      state.queryPropertyFiltersForceRehydrate = true;
      state.queryPropertyFiltersRehydrateSignal =
        (state.queryPropertyFiltersRehydrateSignal ?? 0) + 1;
      state.activePropertyFilters = {};
      state.activePropertyFiltersVisibilityEnabled = false;
      state.queryString = "(all)";
      state.pendingQueryString = undefined;
      state.queryParseError = undefined;
    },
    setQueryMode(state, action) {
      state.queryMode = action.payload;
    },
    loadFromUrl(state) {
      const query = getStartingQuery();
      const hasHash =
        typeof window === "object" &&
        (window.location.href.split("#")[1] ?? "").trim() !== "";
      state.unrepresentable = query.unrepresentable;
      state.queryMode = query.queryMode;
      state.queryPropertyFilters = query.queryPropertyFilters;
      state.queryPropertyFiltersForceRehydrate =
        query.queryPropertyFiltersForceRehydrate;
      if (query.queryPropertyFiltersForceRehydrate) {
        state.queryPropertyFiltersRehydrateSignal =
          (state.queryPropertyFiltersRehydrateSignal ?? 0) + 1;
      }
      state.activePropertyFilters = query.activePropertyFilters;
      state.activePropertyFiltersVisibilityEnabled =
        query.activePropertyFiltersVisibilityEnabled;
      state.queryParameters = query.queryParameters;
      state.queryParseError = query.queryParseError;
      // Only overwrite queryString when the URL has a hash — hash-less
      // parse yields "(all)" and would clobber a cleared query.
      if (hasHash) {
        state.queryString = query.queryString;
      }
    },
    /**
     * Wipe queryString so table fetch guards skip, and strip the URL
     * hash so a subsequent nav doesn't repopulate stale filters.
     * App Shell only.
     */
    clearAppliedQuery(state) {
      state.queryString = "";
      state.pendingQueryString = undefined;
      clearQueryFromUrl();
    },
    setAttributeQuery(state, action) {
      if (action.payload.queryState !== undefined) {
        state.queryParameters = {
          ...(state.unrepresentable ? {} : state.queryParameters),
          [action.payload.attribute]: action.payload.queryState,
        };
        state.unrepresentable = false;
      }
    },
    setQueryPropertyFilters(state, action) {
      state.queryPropertyFilters = action.payload;
      state.unrepresentable = false;
    },
    setQueryPropertyFiltersForceRehydrate(state, action) {
      state.queryPropertyFiltersForceRehydrate = action.payload;
      // Bump the monotonic rehydrate signal every time a caller
      // requests a rehydrate. PropertyFilterInput observes the
      // signal (not the boolean) so races between concurrent
      // producers and PropertyFilterInput's consume-and-reset
      // effect can't drop a rehydrate. Under React 17's unbatched
      // useEffect updates, the boolean can flip true → false → true
      // across separate renders in the same commit; the counter
      // records every "true" request as a distinct edge.
      if (action.payload === true) {
        state.queryPropertyFiltersRehydrateSignal =
          (state.queryPropertyFiltersRehydrateSignal ?? 0) + 1;
      }
    },
    resetActivePropertyFilters(state) {
      state.activePropertyFilters = {};
    },
    setActivePropertyFilter(state, action) {
      state.activePropertyFilters = {
        ...state.activePropertyFilters,
        [action.payload]: true,
      };
    },
    setActivePropertyFiltersVisibilityEnabled(state, action) {
      state.activePropertyFiltersVisibilityEnabled = action.payload;
    },
    unsetAttributeQuery(state, action) {
      if (action.payload.attribute !== undefined) {
        delete state.queryParameters[action.payload.attribute];
      }
    },
    setQueryString(state) {
      if (
        state.pendingQueryString !== undefined &&
        state.pendingQueryString !== ""
      ) {
        const newState = getStateFromString(
          state.pendingQueryString,
          state.queryMode
        );
        writeQueryToUrl(newState.queryString, state.queryMode);
        // Preserve the monotonic signal across Object.assign, then
        // bump if the parsed state requested a rehydrate.
        const preservedSignal = state.queryPropertyFiltersRehydrateSignal;
        Object.assign(state, newState);
        state.queryPropertyFiltersRehydrateSignal =
          preservedSignal +
          (newState.queryPropertyFiltersForceRehydrate ? 1 : 0);
      } else if (!state.unrepresentable) {
        if (state.queryMode === "UIFilter") {
          const queryString = sumQueries(state.queryParameters).toString();
          writeQueryToUrl(queryString);
          state.queryString = queryString;
        }
        if (state.queryMode === "PropertyFilter") {
          const queryString = sumPropertyFilterQueries(
            state.queryPropertyFilters
          ).toString();
          writeQueryToUrl(queryString, "PropertyFilter");
          state.queryString = queryString;
        }
      }
    },
    setPendingQueryString(state, action) {
      state.pendingQueryString = action.payload;
    },
  },
});

export const {
  setQueryMode,
  setQueryPropertyFilters,
  setQueryPropertyFiltersForceRehydrate,
  setActivePropertyFilter,
  setActivePropertyFiltersVisibilityEnabled,
  resetActivePropertyFilters,
  resetFilter,
  setAttributeQuery,
  unsetAttributeQuery,
  setQueryString,
  setPendingQueryString,
  loadFromUrl,
  clearAppliedQuery,
} = generateQuery.actions;

export default generateQuery.reducer;
